import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function ServiceCalls() {
  // 🔥 Context se Data, Role aur Current User
  const { serviceCalls, loading, isAdmin, currentUser } = useWebData();

  // 🔥 Filter States (New Logic)
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC (Fixed)
  // ==========================================
  useEffect(() => {
    if (!serviceCalls) return;

    let result = [...serviceCalls];

    // Search Logic
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";

            const row = `${i.scrId} ${i.hospitalName} ${i.city} ${i.machine} ${i.model} ${i.serialNo} ${i.status} ${i.senderName || i.userName}`.toLowerCase();
            return row.includes(lower) || dateStr.includes(lower);
        });
    }

    // Date Logic
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setFiltered(result);
  }, [serviceCalls, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Date: formatDate(i.createdAt),
        SCR_ID: i.scrId,
        Hospital: i.hospitalName,
        City: i.city || '-',
        Machine: i.machine,
        Model: i.model,
        Serial: i.serialNo,
        Warranty: i.warrantyStatus,
        Problem: i.remark,
        
        // 🔥 Tracking Info
        Status: i.status,
        Resolution_Note: i.resolutionNote || '-',
        Closed_Date: formatDate(i.closedDate),
        Closed_By: i.closedBy || '-',
        
        Engineer: i.senderName || i.userName 
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ServiceCalls");
    XLSX.writeFile(wb, `Service_Log_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  
  // 🔥 UPDATED: Auto-save Closing Details
  const updateStatus = async (id, status) => {
      // Ask for resolution note if closing
      let note = "";
      if (status === 'Closed') {
          note = prompt("Enter Resolution / Spare Notes:", "Problem Resolved");
          if (note === null) return; // Cancelled
      } else if (!window.confirm(`Mark ticket as ${status}?`)) {
          return;
      }

      try {
          const updatePayload = { status };
          
          // Agar Close kiya hai to Date aur Naam save karo
          if(status === 'Closed') {
              updatePayload.closedDate = new Date().toISOString();
              updatePayload.closedBy = currentUser?.name || "Admin";
              updatePayload.resolutionNote = note;
          }

          await updateDoc(doc(db, "service_calls", id), updatePayload);
          
          if(viewData && viewData.id === id) {
              setViewData({...viewData, ...updatePayload});
          }
      } catch(e) { alert("Error updating status"); }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "service_calls", editData.id), {
              hospitalName: editData.hospitalName,
              city: editData.city, 
              machine: editData.machine,
              model: editData.model,
              serialNo: editData.serialNo,
              remark: editData.remark,
              status: editData.status,
              // Editable Closing Info
              resolutionNote: editData.resolutionNote,
              closedDate: editData.closedDate
          });
          alert("Ticket Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this ticket?")) await deleteDoc(doc(db, "service_calls", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const date = new Date(iso);
      return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#e74c3c", display:"flex", alignItems:"center"}}>
                📞 Service Calls <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Ticket..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#e74c3c", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>SCR ID</th>
                    <th style={thStyle}>Hospital & City</th>
                    <th style={thStyle}>Machine & Model</th>
                    <th style={thStyle}>Serial</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No tickets found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>{row.scrId}</td>
                    
                    <td style={{...tdStyle, textAlign:"left"}}>
                        <div style={{fontWeight:"bold"}}>{row.hospitalName}</div>
                        <div style={{fontSize:"11px", color:"#7f8c8d"}}>{row.city || '-'}</div>
                    </td>

                    <td style={{...tdStyle, textAlign:"left"}}>
                        <div style={{fontWeight:"bold", color:"#333"}}>{row.machine}</div>
                        <div style={{fontSize:"11px", color:"#555"}}>{row.model}</div>
                    </td>

                    <td style={tdStyle}>
                        {row.serialNo}
                        <br/>
                        <span style={{fontSize:"9px", color: row.warrantyStatus==='Out of Warranty'?'red':'green'}}>
                            {row.warrantyStatus === 'Out of Warranty' ? 'Exp' : 'War'}
                        </span>
                    </td>

                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Closed'?'#e8f5e9':'#fff3e0',
                            color: row.status==='Closed'?'green':'orange'
                        }}>
                            {row.status}
                        </span>
                    </td>
                    
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            {isAdmin && row.status !== 'Closed' && (
                                <button onClick={()=>updateStatus(row.id,'Closed')} style={btnCheck} title="Close Ticket">✔</button>
                            )}
                            <button onClick={() => setViewData(row)} style={btnIcon} title="View">👁️</button>
                            {isAdmin && (
                                <>
                                    <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                                    <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL (UPDATED) */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#e74c3c"}}>📞 Ticket Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px"}}>
                    <div>
                        <p><strong>📅 Opened:</strong> {formatDate(viewData.createdAt)}</p>
                        <p><strong>🆔 SCR ID:</strong> {viewData.scrId}</p>
                        <p><strong>🏥 Hospital:</strong> {viewData.hospitalName}</p>
                        <p><strong>📍 City:</strong> {viewData.city || '-'}</p>
                    </div>
                    <div>
                        <p><strong>⚙️ Machine:</strong> {viewData.machine}</p>
                        <p><strong>🏷️ Model:</strong> {viewData.model}</p>
                        <p><strong>🔢 Serial:</strong> {viewData.serialNo}</p>
                        <p><strong>🛡️ Warranty:</strong> {viewData.warrantyStatus}</p>
                    </div>
                </div>
                
                <hr style={{border:"0.5px solid #eee"}}/>
                <p><strong>🏠 Address:</strong> {viewData.address || '-'}</p>
                
                <div style={{background:"#fff3e0", padding:"10px", marginTop:"10px", borderRadius:"5px", borderLeft:"4px solid #e74c3c"}}>
                    <p style={{margin:0, fontWeight:"bold", color:"#e74c3c"}}>📝 Problem Description:</p>
                    <p style={{marginTop:"5px"}}>{viewData.remark}</p>
                </div>

                {/* 🔥 CLOSING DETAILS */}
                {viewData.status === 'Closed' && (
                    <div style={{background:"#e8f5e9", padding:"10px", marginTop:"10px", borderRadius:"5px", borderLeft:"4px solid green"}}>
                        <h4 style={{margin:"0 0 5px 0", color:"green", fontSize:"12px"}}>✅ Resolution & Closing</h4>
                        <p><strong>🛠️ Action Taken:</strong> {viewData.resolutionNote || "No notes"}</p>
                        <p><strong>👤 Closed By:</strong> {viewData.closedBy || "Admin"}</p>
                        <p><strong>📅 Date:</strong> {formatDate(viewData.closedDate)}</p>
                    </div>
                )}

                <div style={{marginTop:"10px", display:"flex", justifyContent:"space-between"}}>
                    <span>🔥 Status: <strong>{viewData.status}</strong></span>
                    <span>👷 Engineer: <strong>{viewData.senderName || viewData.userName}</strong></span>
                </div>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Ticket</h3>
            
            <label style={labelStyle}>Hospital Name</label>
            <input type="text" value={editData.hospitalName} onChange={(e)=>setEditData({...editData, hospitalName:e.target.value})} style={modalInput}/>
            
            <label style={labelStyle}>Machine Details</label>
            <div style={{display:"flex", gap:"5px"}}>
                <input type="text" value={editData.machine} onChange={(e)=>setEditData({...editData, machine:e.target.value})} style={modalInput} placeholder="Machine"/>
                <input type="text" value={editData.model} onChange={(e)=>setEditData({...editData, model:e.target.value})} style={modalInput} placeholder="Model"/>
            </div>

            <label style={labelStyle}>Problem / Remark</label>
            <textarea value={editData.remark} onChange={(e)=>setEditData({...editData, remark:e.target.value})} style={{...modalInput, height:"50px"}}></textarea>

            <label style={labelStyle}>Resolution Note (Spare/Action)</label>
            <textarea value={editData.resolutionNote} onChange={(e)=>setEditData({...editData, resolutionNote:e.target.value})} style={{...modalInput, height:"50px"}} placeholder="Parts replaced..."></textarea>

            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Status</label>
                    <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                        <option value="Open">Open</option>
                        <option value="Closed">Closed</option>
                        <option value="Pending Parts">Pending Parts</option>
                    </select>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Closed Date</label>
                    <input type="date" value={editData.closedDate ? editData.closedDate.split('T')[0] : ''} onChange={(e)=>setEditData({...editData, closedDate:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnCheck={backgroundColor:"#27ae60", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"450px", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#e74c3c", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default ServiceCalls;